import { device } from 'utils/device';
import Config from 'config/settings';
import { DOMHelper } from 'core/DOMHelper';
import { SectionType } from 'app/data/Enums';
import { LoaderGraphic } from 'app/module/global/LoaderGraphic';
import { TextScramble } from 'utils/TextScramble';
import DelayHelper from 'utils/timers';
import fastdom from 'fastdom';

import { PreloadViewDeps } from './preload.types';

/**
 * DOM side of the preloader: the opening safari/fullscreen message body
 * classes, the -ready / -hidden / -homeready / -preloadcomplete class
 * choreography (the Playwright smoke suite waits for .preload.-homeready —
 * applied in hide() — before clicking .home__action__button__center; do NOT
 * change when those classes land), the loader-message text scramble, the
 * LoaderGraphic hand-off and the preloader DOM removal. Event wiring and the
 * boot flow stay in PreloadController.
 */
export class PreloadView {
	private _dom: DOMHelper;
	private _deps: PreloadViewDeps;
	private _container: Element;

	/** Resolved by PreloadController.modulesMounted (module manager access stays there) */
	public loaderGraphic: LoaderGraphic;

	private needsSafariMessage: boolean = false;
	private needsFullScreenButton: boolean = false;
	private needsMessageDivider: boolean = false;

	private _textScrable: TextScramble;
	//Hand-toggled mode knob for the (dead, see preloaderText) message loop:
	//'loading' blanked the text each tick, 'all' cycled the _text list. Declared
	//as the union so the 'loading' branch below typechecks; the value has always
	//been 'all':
	private readonly _textVersion: 'all' | 'loading' = 'all';
	private _textInterval = null;
	private _textIndex = 0;
	private readonly _text = [
		'INITIATING',
		'DEBUGING',
		'PREPARING',
		'BUILDING',
		'CHECKING',
		'BUFFERING',
		'SYNCING',
		'MOVING',
		'LOADING'
	];

	constructor(dom: DOMHelper, deps: PreloadViewDeps) {
		this._dom = dom;
		this._deps = deps;
	}

	public setContainer(container: Element): void {
		this._container = container;
	}

	//Opening messages (safari / fullscreen)
	//-------------------------------------------------------------------------------------

	/** The showLoaderMessagesAndButtons block of the old setIntialState (section state stays in the controller) */
	public setupOpeningMessages(): void {
		let showLoaderMessagesAndButtons = window.isHome && !window.skipIntroClick;

		if (showLoaderMessagesAndButtons) {
			let isIOS = this.iOS();
			let isAndroid = this.isAndroidCheck();
			let isIpad = this.checkIsIpad();
			let iSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
			let isFullScreen = iSafari || isAndroid ? false : !this.isAlreadyFullScreen();
			isFullScreen = false;

			device.isIOS = isIOS;
			device.isAndroid = isAndroid;
			device.isSafari = iSafari;
			device.isMobile = isAndroid || isIOS;

			//Show browser message to desktop iOS:
			this.needsSafariMessage = iSafari && !isIOS && !isIpad;

			//All setups including iPad apart from mobile iOS can have fullscreen button
			this.needsFullScreenButton =
				!document.fullscreenElement &&
				(!isIOS || isIpad) &&
				(document.documentElement.requestFullscreen ||
					document.documentElement.msRequestFullscreen ||
					document.documentElement.mozRequestFullScreen ||
					document.documentElement.webkitRequestFullscreen)
					? true
					: false;

			console.log('this.needsFullScreenButton ', this.needsFullScreenButton);

			this.needsMessageDivider = this.needsSafariMessage && this.needsFullScreenButton && !isFullScreen;

			const $body = document.querySelector('body');

			//Needs to show message:
			if (this.needsFullScreenButton || this.needsMessageDivider) {
				this._dom.addClass($body, '-preloader-has-messsage');
			}

			if (this.needsFullScreenButton && !isFullScreen) {
				this._dom.addClass($body, '-has-fullscreen-button');
			}

			if (this.needsSafariMessage) {
				this._dom.addClass($body, '-preloader-has-safari-message');
			}

			if (this.needsMessageDivider) {
				this._dom.addClass($body, '-preloader-has-message-divider');
			}
		}
	}

	private isAlreadyFullScreen() {
		const windowWidth = window.innerWidth * window.devicePixelRatio;
		const windowHeight = window.innerHeight * window.devicePixelRatio;
		const screenWidth = window.screen.width;
		const screenHeight = window.screen.height;
		if (windowWidth / screenWidth >= 0.95 && windowHeight / screenHeight >= 0.95) {
			return true;
		} else {
			return false;
		}
	}

	private isAndroidCheck() {
		return /Android|webOS/i.test(navigator.userAgent);
	}

	private iOS() {
		return (
			['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
			// iPad on iOS 13 detection
			(navigator.userAgent.includes('Mac') && 'ontouchend' in document)
		);
	}

	private checkIsIpad() {
		return (
			['iPad Simulator', 'iPad'].includes(navigator.platform) ||
			// iPad on iOS 13 detection
			(navigator.userAgent.includes('Mac') && 'ontouchend' in document && window.innerWidth > 700) ||
			(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && window.innerWidth > 700)
		);
	}

	//Loader messages
	//-------------------------------------------------------------------------------------

	public preloaderText(): void {
		if (this._textScrable) {
			this._textScrable.setText('');
		}

		return;

		//NOTE: dead (unreachable) tail moved intact from
		//PreloadController._preloaderText — everything below the return above has
		//never run; it is the only place _textScrable / _textInterval are ever
		//assigned, so the guard above never fires either.
		this._textScrable = new TextScramble(document.querySelector('.home__action__text'), 20);
		this._textScrable.setText('LOADING');

		this._textInterval = setInterval(() => {
			if (this._textVersion == 'loading') {
				let text = this._textIndex == 0 ? '' : '';
				this._textIndex++;

				if (this._textIndex >= 2) {
					this._textIndex = 0;
				}

				this._textScrable.setText(text);

				return;
			}

			let text = this._text[this._textIndex];
			this._textIndex++;

			if (this._textIndex >= this._text.length) {
				this._textIndex = 0;
			}

			this._textScrable.setText(text);
		}, 1500);
	}

	//Ready / show / hide choreography
	//-------------------------------------------------------------------------------------

	/** The DOM half of the old _doPreloader (timers + flow stay in the controller) */
	public markReady(): void {
		(this._container as HTMLElement).style.display = 'block';
		this._dom.addClass(this._container, '-ready');
	}

	/** Show the view */
	public show(): void {
		this.loaderGraphic.animateIn();
	}

	/** Loading is completed so hide: */
	public hide(): void {
		clearInterval(this._textInterval);

		const $body = document.querySelector('body');
		this._dom.addClass($body, '-preloadcomplete');

		if (this._deps.getSection() === SectionType.HOME) {
			this._dom.addClass(this._container, '-homeready');
			return;
		}

		DelayHelper.setTimeout(() => {
			gsap.TweenMax.to(this._container, 0.5, { autoAlpha: 0, delay: 0.5, ease: Config.linearEase });
			this._deps.showUI();
			if (this.loaderGraphic) {
				this.loaderGraphic.animateOut();
				this._dom.removeClass(document.querySelector('body'), '-preloading');
			}
			this._removeDOM();
		}, 100);
	}

	private _removeDOM() {
		if (this._deps.getSection() === SectionType.HOME) {
			fastdom.mutate(() => {
				(this._container as HTMLElement).style.pointerEvents = 'none';
				(this._container as HTMLElement).style.display = 'none';
			});
		}

		DelayHelper.setTimeout(() => {
			if (this._container) {
				if (this._deps.getSection() !== SectionType.HOME) {
					fastdom.mutate(() => {
						(this._container as HTMLElement).style.pointerEvents = 'none';
						(this._container as HTMLElement).style.display = 'none';
						this._container.remove();
						this._container = null;
					});
				}
			}

			this._textScrable = null;
		}, 1000);

		return;
	}

	public hideForIntro() {
		this._dom.addClass(this._container, '-hidden');
		this.loaderGraphic.animateOut();

		DelayHelper.setTimeout(() => {
			this._deps.showUI();
			this._dom.removeClass(document.querySelector('body'), '-preloading');
			this._removeDOM();
		}, 2600);
	}

	//Loader graphic pass-throughs
	//-------------------------------------------------------------------------------------

	/**
	 * Particles can take a while to add, so factor them into loading:
	 */
	public homeParticlesCreated() {
		this.loaderGraphic.homeParticlesCreated();
	}

	/**
	 * Particles can take a while to add, so factor them into loading:
	 */
	public homePixelDataReady() {
		this.loaderGraphic.homePixelDataReady();
	}
}
